import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/Button';
import LanguageSelector from './LanguageSelector';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

const PublicNavbar = () => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: t('layout.navbar.links.features'), path: '/features' },
    { name: t('layout.navbar.links.pricing'), path: '/pricing' },
    { name: t('layout.navbar.links.solutions'), path: '/solutions' },
    { name: t('layout.navbar.links.resources'), path: '/resources' },
    { name: t('layout.navbar.links.ca_services'), path: '/ca-services' },
  ];

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b border-transparent",
        isScrolled ? "bg-white/80 backdrop-blur-md border-slate-200/50 py-3" : "bg-transparent py-5"
      )}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between relative">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center shadow-lg shadow-slate-900/10 transition-transform group-hover:scale-105">
              <span className="text-white font-bold text-lg leading-none pb-0.5">S</span>
            </div>
            <span className="text-xl font-bold text-slate-900 tracking-tight">soma.</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="absolute inset-0 flex justify-center items-center pointer-events-none">
            <nav className="hidden md:flex items-center gap-8 pointer-events-auto">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={cn(
                    "inline-flex items-center h-8 text-sm font-medium transition-colors hover:text-slate-900",
                    location.pathname === link.path ? "text-slate-900" : "text-slate-500"
                  )}
                >
                  {link.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* Right Actions */}
          <div className="hidden md:flex items-center gap-4">
            <div className="hidden lg:block">
              <LanguageSelector />
            </div>
            <div className="h-4 w-px bg-slate-200 hidden lg:block" />
            <Link to="/login">
              <Button variant="ghost" size="md" className="text-slate-600 hover:text-slate-900">
                Sign In
              </Button>
            </Link>
            <Link to="/register">
              <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white shadow-lg shadow-slate-900/10">
                Start Free
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="flex items-center gap-4 md:hidden">
            <LanguageSelector />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div className="absolute top-full left-0 right-0 bg-white border-b border-slate-100 shadow-xl md:hidden animate-in slide-in-from-top-2 fade-in duration-200">
          <div className="flex flex-col p-4 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                className={cn(
                  "px-4 py-3 rounded-lg text-sm font-medium transition-colors",
                  location.pathname === link.path
                    ? "bg-slate-50 text-slate-900"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                )}
              >
                {link.name}
              </Link>
            ))}
            <div className="h-px bg-slate-100 my-2" />
            <div className="flex flex-col gap-2">
              <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                <Button variant="outline" className="w-full justify-center">
                  Sign In
                </Button>
              </Link>
              <Link to="/register" onClick={() => setIsMenuOpen(false)}>
                <Button className="w-full justify-center bg-slate-900 hover:bg-slate-800">
                  Start Free
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default PublicNavbar;
